import React, { useEffect, useMemo, useState } from "react";

import DoneIcon from "@mui/icons-material/Done";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Table as MuiTable,
  TableCell as MuiTableCell,
  TableCellProps as MuiTableCellProps,
  Paper,
  Select,
  Stack,
  Switch,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useActionHook } from "hooks";
import { useDealActions } from "store";
import { AltiusErrors, MeasureCalcFieldsTable, UpdateMeasureCalcFieldsPayload } from "store/types";

import { MaterialTooltip } from "../../tooltips/MaterialTooltip";
import { toastify } from "utils/tyostify";

export interface Props {
  searchValue: string;
}

export const RoleTable: React.FC<Props> = ({ searchValue }) => {
  const { getMeasureCalcFieldsTable, updateMeasureCalcFieldsTable, getMeasureParametersAction } = useDealActions();

  const {
    mutate: save,
    isLoading: isSaving,
    isSuccess
  } = useMutation<void, AltiusErrors, UpdateMeasureCalcFieldsPayload>((data) => updateMeasureCalcFieldsTable(data));

  const {
    action: getMeasureCalcFieldsTableAction,
    data: measureCalcFieldTable,
    loading
  } = useActionHook(getMeasureCalcFieldsTable);

  const [changes, setChanges] = useState<
    Array<{ measure_id: number; calc_field: string; role: number; prime_role?: string | null }>
  >([]);

  useEffect(() => {
    // getPrimeRolesAction();
    getMeasureCalcFieldsTableAction();
  }, []);

  const handleChange = (measureId: number, calcField: string, role: number): void => {
    const newChanges = changes.filter((change) => change.measure_id !== measureId || change.calc_field !== calcField);
    newChanges.push({ measure_id: measureId, calc_field: calcField, role });
    setChanges(newChanges);
  };

  const handleSave = () => {
    save(changes, {
      onSuccess: () => {
        getMeasureParametersAction();
        toastify({ message: "Measure roles updated successfully", type: "success" });
      }
    });
  };

  const table = measureCalcFieldTable || { rows: [], columns: [] };

  // Memoize the search value to prevent unnecessary re-computations
  const normalizedSearchValue = useMemo(() => {
    return searchValue?.toLowerCase().trim() || "";
  }, [searchValue]);

  // Memoize the filtered rows with optimized filtering
  const filteredRows = useMemo(() => {
    if (!normalizedSearchValue) return table.rows;

    return table.rows.filter((row) => {
      const measure = (row._measure || "").toLowerCase();
      const primeRole = (row._prime_role || "").toLowerCase();

      return measure.includes(normalizedSearchValue) || primeRole.includes(normalizedSearchValue);
    });
  }, [table.rows, normalizedSearchValue]);

  return (
    <Stack
      sx={{
        p: "16px",
        borderRadius: "8px",
        overflow: "auto",
        bgcolor: "white",
        alignItems: "flex-end",
        gap: "20px",
        flexGrow: 1,
        boxShadow: "0 1px 2px 0 #1212171a",
        margin: "0 2px 2px 2px"
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          height: "100%",
          border: "1px solid #dae0e2",
          borderTop: "none",
          alignItems: "center",
          position: "relative"
        }}
      >
        {loading && (
          <Stack
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 10,
              borderRadius: "8px"
            }}
          >
            <CircularProgress size={50} />
          </Stack>
        )}
        <MuiTable
          sx={{
            ".MuiTableCell-root": {
              ":first-child": {
                position: "sticky",
                left: 0,
                zIndex: 3
              }
            },
            ".MuiTableCell-body": {
              padding: "14px 26px",
              transition: "all 0.2s linear",
              backgroundColor: "#f5f7f7",
              ":last-child": { width: "4%" },
              ":first-child": {
                minWidth: "200px",
                maxWidth: "200px",
                padding: "14px 0px 14px 17px",
                zIndex: 1,
                cursor: "pointer"
              },
              position: "relative"
            }
          }}
          stickyHeader
        >
          <TableHead sx={{ bgcolor: "#edf1f9" }}>
            <TableRow sx={{ height: "44px" }}>
              {table.columns.map((column) => (
                <MuiTableCell sx={commonHeadCellSx} key={column.key}>
                  <Stack direction="row" gap="5px" alignItems="center">
                    {column.title}
                    {!!column.note && (
                      <MaterialTooltip isShow placement="right" title={column.note}>
                        <HelpOutlineIcon sx={{ fontSize: "14px" }} />
                      </MaterialTooltip>
                    )}
                  </Stack>
                </MuiTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow
                sx={{
                  height: "44px",
                  ".MuiButtonBase-root": { opacity: 0, transition: "all 0.3s linear" },
                  ":hover": {
                    ".MuiTableCell-body": { backgroundColor: "#dae0e2" },
                    ".MuiButtonBase-root": { opacity: 1 }
                  }
                }}
                key={row.id}
              >
                {table.columns.map((column) => {
                  const change = changes.find(
                    (change) => change.measure_id === row._id && change.calc_field === column.key
                  );
                  const value = change ? change.role : row[column.key];
                  return (
                    <TableCell
                      sx={commonBodyCellSx}
                      key={column.key}
                      row={row}
                      column={column}
                      cellValue={value}
                      onChange={handleChange}
                    />
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <Stack direction="row" gap="5px" alignItems="center">
        {isSaving && <CircularProgress size={20} />}
        {isSuccess && <DoneIcon />}
        <Button variant="contained" disabled={isSaving} onClick={handleSave}>
          Save
        </Button>
      </Stack>
    </Stack>
  );
};

type TableCellProps = Omit<MuiTableCellProps, "onChange"> & {
  row: MeasureCalcFieldsTable["rows"][number];
  column: MeasureCalcFieldsTable["columns"][number];
  cellValue: number;
  onChange: (measureId: number, calcField: string, value: number) => void;
};

const TableCell = React.memo(({ row, column, cellValue, onChange, ...cellProps }: TableCellProps) => {
  if (typeof cellValue === "string" || cellValue === null) {
    return (
      <MuiTableCell {...cellProps}>
        <Typography>{cellValue}</Typography>
      </MuiTableCell>
    );
  }
  if (!column.options) {
    return (
      <MuiTableCell {...cellProps}>
        <Switch
          disabled={!row._prime_role}
          checked={!!cellValue}
          onChange={(e, checked) => onChange(row._id, column.key, checked ? 1 : 0)}
        />
      </MuiTableCell>
    );
  }
  if (column.options) {
    return (
      <MuiTableCell {...cellProps}>
        <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
          <Select
            inputProps={{ "aria-label": "Without label" }}
            value={cellValue}
            disabled={!row._prime_role}
            onChange={(e) => onChange(row._id, column.key, e.target.value as number)}
          >
            <MenuItem value={0}>
              <em>None</em>
            </MenuItem>
            {column.options.map((opt, index) => (
              <MenuItem key={opt} value={index + 1}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </MuiTableCell>
    );
  }
});

const commonBodyCellSx: TableCellProps["sx"] = {
  color: "#013951",
  position: "relative",
  borderLeft: "4px solid transparent",
  padding: "0 23px",
  verticalAlign: "middle",
  fontSize: "15px",
  border: "none",
  borderBottom: "1px solid #bbc5c7",
  minWidth: "140px",
  maxWidth: "240px"
};

const commonHeadCellSx: TableCellProps["sx"] = {
  whiteSpace: "nowrap",
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "16px",
  padding: "0 23px !important",
  verticalAlign: "middle",
  minWidth: "140px",
  maxWidth: "240px",
  fontWeight: "normal",
  border: "none"
};
